import type { MapDocument, MapGeometry } from "../shared/types";
import { mapPreviewOpacity } from "../shared/mapPresentation";
import {
  venueMapLabelPathBounds,
  venueMapTextBounds,
  paintVenueMapText,
  type VenueMapTextRun,
  type VenueMapLabelBounds,
} from "./venueMapWebGLLabels";

type EntranceLabel = {
  entrance: MapGeometry["entrances"][number];
  path?: string;
  text: VenueMapTextRun[];
  bounds: VenueMapLabelBounds;
};
type PackedEntrance = EntranceLabel & {
  x: number;
  y: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

const textStrokeWidth = 3;
// Transparent gutters isolate each label through the mip levels used when the
// full map is fitted to a phone. They are part of the billboard's UV bounds.
const atlasPadding = 16;
const nextPowerOfTwo = (value: number) =>
  2 ** Math.ceil(Math.log2(Math.max(1, value)));

function packEntrances(labels: EntranceLabel[], scale: number, limit: number) {
  const tiles = labels.map((label) => {
    const left = Math.floor(label.bounds.minX * scale) - atlasPadding;
    const top = Math.floor(label.bounds.minY * scale) - atlasPadding;
    return {
      ...label,
      x: 0,
      y: 0,
      left,
      top,
      width: Math.ceil(label.bounds.maxX * scale) + atlasPadding - left,
      height: Math.ceil(label.bounds.maxY * scale) + atlasPadding - top,
    };
  });
  const minimumWidth = nextPowerOfTwo(
    Math.max(1, ...tiles.map((tile) => tile.width)),
  );
  let best:
    { width: number; height: number; labels: PackedEntrance[] } | undefined;
  for (let width = minimumWidth; width <= limit; width *= 2) {
    let x = 0;
    let y = 0;
    let shelfHeight = 0;
    const packed = tiles.map((tile) => {
      if (x + tile.width > width) {
        x = 0;
        y += shelfHeight;
        shelfHeight = 0;
      }
      const item = { ...tile, x, y };
      x += tile.width;
      shelfHeight = Math.max(shelfHeight, tile.height);
      return item;
    });
    const height = nextPowerOfTwo(y + shelfHeight);
    if (
      height <= limit &&
      (!best ||
        width * height < best.width * best.height ||
        (width * height === best.width * best.height &&
          Math.max(width, height) < Math.max(best.width, best.height)))
    ) {
      best = { width, height, labels: packed };
    }
  }
  return best;
}

/**
 * Static RGBA entrance lettering, drawn from licensed paths once per context.
 * Each vertex is [anchorX, anchorY, anchorZ, offsetX, offsetY, u, v, opacity]. Anchors
 * follow the floor projection; offsets remain screen upright in source units.
 */
export function buildVenueMapWebGLEntranceAtlas(
  map: MapDocument,
  maxTextureSize: number,
): {
  canvas: HTMLCanvasElement;
  vertices: Float32Array;
} {
  if (!Number.isFinite(maxTextureSize) || maxTextureSize < 64) {
    throw new Error("Map entrance texture size is unsupported");
  }
  const limit = 2 ** Math.floor(Math.log2(Math.min(2048, maxTextureSize)));
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Map entrance texture could not be created");
  const labels = map.geometry.entrances.map((entrance) => {
    const path = map.labels.entrances[entrance.id];
    const text: VenueMapTextRun[] = [
      { text: entrance.label, x: 8, y: 3, size: 9, weight: 600, align: "left" },
    ];
    const bounds = path
      ? venueMapLabelPathBounds(path)
      : venueMapTextBounds(context, text);
    const halfStroke = textStrokeWidth / 2;
    return {
      entrance,
      path,
      text,
      bounds: {
        minX: bounds.minX - halfStroke,
        minY: bounds.minY - halfStroke,
        maxX: bounds.maxX + halfStroke,
        maxY: bounds.maxY + halfStroke,
      },
    };
  });
  let scale = 4;
  let packed = packEntrances(labels, scale, limit);
  while (!packed && scale > 0.25) {
    scale = Math.max(0.25, scale * 0.75);
    packed = packEntrances(labels, scale, limit);
  }
  if (!packed)
    throw new Error("Map entrances exceed the supported texture size");

  canvas.width = packed.width;
  canvas.height = packed.height;
  context.lineWidth = textStrokeWidth;
  context.lineJoin = "round";
  const vertices: number[] = [];
  for (const tile of packed.labels) {
    context.setTransform(
      scale,
      0,
      0,
      scale,
      tile.x - tile.left,
      tile.y - tile.top,
    );
    const path = tile.path ? new Path2D(tile.path) : undefined;
    context.strokeStyle = "#f9f8f4";
    if (path) context.stroke(path);
    else paintVenueMapText(context, tile.text, true);
    context.fillStyle = "#53665e";
    if (path) context.fill(path);
    else paintVenueMapText(context, tile.text);
    const corners = [
      [0, 0],
      [tile.width, 0],
      [tile.width, tile.height],
      [0, tile.height],
    ];
    for (const corner of [0, 1, 2, 0, 2, 3]) {
      const [dx, dy] = corners[corner];
      vertices.push(
        tile.entrance.x,
        tile.entrance.y,
        0,
        (tile.left + dx) / scale,
        (tile.top + dy) / scale,
        (tile.x + dx) / canvas.width,
        (tile.y + dy) / canvas.height,
        mapPreviewOpacity(tile.entrance),
      );
    }
  }
  return { canvas, vertices: new Float32Array(vertices) };
}
